import {
  captureBackendApiError,
  getApiBaseUrl,
  getRpcAuthToken,
  RpcError,
  rpcCall,
} from './rpc-client';

export type EmployeeRating = {
  count: number;
  score: string;
};

export type EmployeeProfile = {
  id: number;
  firstName?: string;
  lastName?: string;
  locale?: string;
  countryCode?: string;
  tin?: string;
  phone: string;
  email?: string;
  emailIsVerified: boolean;
  showRating?: boolean;
  avatarUrl?: string;
  isOnboarded: boolean;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeWallet = {
  accBalance?: string;
  balance?: string;
  currency?: string;
  frozenBalance?: string;
  id?: number;
  uuid: string;
};

export type EmployeeTipStats = {
  depositCountDay?: number;
  depositSumDay?: string;
  depositSumMonth?: string;
  withdrawalSumMonth?: string;
};

type EmployeeTipStatsRpcResult = EmployeeTipStats & {
  DepositCountDay?: number;
  DepositSumDay?: string;
  DepositSumMonth?: string;
  WithdrawalSumMonth?: string;
};

type EmployeeDashboardRpcResult = {
  Profile?: EmployeeProfile;
  profile?: EmployeeProfile;
  TipStats?: EmployeeTipStatsRpcResult;
  tipStats?: EmployeeTipStatsRpcResult;
  Wallet?: EmployeeWallet;
  wallet?: EmployeeWallet;
};

export type EmployeeDashboard = {
  profile: EmployeeProfile;
  tipStats: EmployeeTipStats;
  wallet: EmployeeWallet;
};

export type UpdateEmployeeProfileInput = {
  firstName?: string | null;
  lastName?: string | null;
  tin?: string | null;
  locale?: string | null;
  showRating?: boolean | null;
  isOnboarded?: boolean | null;
};

export type UpdateEmployeeEmailInput = {
  email: string;
};

export type EmployeeGoal = {
  text: string;
  targetAmount: number;
  currentAmount: number;
};

export type EmployeeTransaction = {
  uuid: string;
  amount: string;
  feeAmount: string;
  type: string;
  status: string;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GetEmployeeTransactionsInput = {
  limit?: number;
  type?: string;
};

export type EmployeeTransactions = {
  hasMoreItems: boolean;
  items: EmployeeTransaction[];
};

export type UploadEmployeeAvatarInput = {
  avatar: Blob;
  fileName?: string;
  onUploadProgress?: (progressPercent: number) => void;
};

export type UpdateEmployeeGoalInput = {
  text: string;
  targetAmount: number;
};

export type EmployeeWithdrawInput = {
  amount: number;
  idempotencyKey: string;
};

type EmployeeReferralsCountRpcResult = {
  chancePercent?: number;
  chance_percent?: number;
  completedCount?: number;
  completed_count?: number;
  converted?: number;
  pendingCount?: number;
  pending_count?: number;
  ticketsCount?: number;
  tickets_count?: number;
  total?: number;
  totalReferrals?: number;
  total_referrals?: number;
};

export type EmployeeReferralsCount = {
  chancePercent: number;
  completedCount: number;
  converted: number;
  pendingCount: number;
  ticketsCount: number;
  total: number;
};

export type EmployeeReferral = {
  firstName?: string;
  id: number;
  isConverted: boolean;
  lastName?: string;
};

export type GetEmployeeReferralsInput = {
  isConverted?: boolean;
  limit?: number;
  offset?: number;
};

export type EmployeeReferrals = {
  hasMoreItems: boolean;
  items: EmployeeReferral[];
};

type EmployeeReferralsRpcResult = {
  HasMoreItems?: boolean;
  Items?: EmployeeReferral[];
  hasMoreItems?: boolean;
  items?: EmployeeReferral[];
};

type UploadEmployeeAvatarHttpResponse = {
  error?: string;
  message?: string;
  result?: string;
  status?: string;
};

function resolveEmployeeDashboardProfile(
  result: EmployeeDashboardRpcResult,
): EmployeeProfile {
  const profile = result.profile ?? result.Profile;

  if (!profile) {
    const error = new Error(
      'Employee.GetDashboard response is missing profile',
    );
    captureBackendApiError(error, {
      endpoint: 'Employee.GetDashboard',
      transport: 'jsonrpc',
    });
    throw error;
  }

  return profile;
}

function resolveEmployeeDashboardWallet(
  result: EmployeeDashboardRpcResult,
): EmployeeWallet {
  const wallet = result.wallet ?? result.Wallet;

  if (!wallet) {
    const error = new Error('Employee.GetDashboard response is missing wallet');
    captureBackendApiError(error, {
      endpoint: 'Employee.GetDashboard',
      transport: 'jsonrpc',
    });
    throw error;
  }

  return wallet;
}

function normalizeEmployeeTipStats(
  tipStats: EmployeeTipStatsRpcResult | undefined,
): EmployeeTipStats {
  if (!tipStats) {
    return {};
  }

  return {
    depositCountDay:
      tipStats.depositCountDay ?? tipStats.DepositCountDay ?? undefined,
    depositSumDay:
      tipStats.depositSumDay ?? tipStats.DepositSumDay ?? undefined,
    depositSumMonth:
      tipStats.depositSumMonth ?? tipStats.DepositSumMonth ?? undefined,
    withdrawalSumMonth:
      tipStats.withdrawalSumMonth ?? tipStats.WithdrawalSumMonth ?? undefined,
  };
}

function resolveNonNegativeInteger(
  ...candidates: Array<number | undefined>
): number {
  for (const candidate of candidates) {
    if (
      typeof candidate === 'number' &&
      Number.isFinite(candidate) &&
      candidate >= 0
    ) {
      return Math.trunc(candidate);
    }
  }

  return 0;
}

function normalizeEmployeeReferralsCount(
  result: EmployeeReferralsCountRpcResult,
): EmployeeReferralsCount {
  const total = resolveNonNegativeInteger(
    result.total,
    result.totalReferrals,
    result.total_referrals,
  );
  const converted = resolveNonNegativeInteger(result.converted);
  const completedCount = resolveNonNegativeInteger(
    result.completedCount,
    result.completed_count,
    converted,
  );
  const pendingCount = resolveNonNegativeInteger(
    result.pendingCount,
    result.pending_count,
    Math.max(total - completedCount, 0),
  );

  return {
    chancePercent: resolveNonNegativeInteger(
      result.chancePercent,
      result.chance_percent,
    ),
    completedCount,
    converted,
    pendingCount,
    ticketsCount: resolveNonNegativeInteger(
      result.ticketsCount,
      result.tickets_count,
      completedCount,
    ),
    total,
  };
}

function normalizeEmployeeReferrals(
  result: EmployeeReferralsRpcResult,
): EmployeeReferrals {
  return {
    hasMoreItems: Boolean(result.hasMoreItems ?? result.HasMoreItems ?? false),
    items: result.items ?? result.Items ?? [],
  };
}

export function getEmployeeDashboard(
  timeZone: string,
): Promise<EmployeeDashboard> {
  return rpcCall<EmployeeDashboardRpcResult>('Employee.GetDashboard', {
    timeZone,
  }).then(result => ({
    profile: resolveEmployeeDashboardProfile(result),
    tipStats: normalizeEmployeeTipStats(result.tipStats ?? result.TipStats),
    wallet: resolveEmployeeDashboardWallet(result),
  }));
}

export function getEmployeeProfile(): Promise<EmployeeProfile> {
  return rpcCall<EmployeeProfile>('Employee.GetProfile');
}

export function getEmployeeRating(): Promise<EmployeeRating> {
  return rpcCall<EmployeeRating>('Employee.GetRating');
}

export function updateEmployeeProfile(
  input: UpdateEmployeeProfileInput,
): Promise<EmployeeProfile> {
  return rpcCall<EmployeeProfile>('Employee.UpdateProfile', input);
}

export function updateEmployeeEmail(
  input: UpdateEmployeeEmailInput,
): Promise<EmployeeProfile> {
  return rpcCall<EmployeeProfile>('Employee.UpdateEmail', input);
}

export function getEmployeeGoal(): Promise<EmployeeGoal> {
  return rpcCall<EmployeeGoal>('Employee.GetGoal');
}

export function getEmployeeReferralsCount(): Promise<EmployeeReferralsCount> {
  return rpcCall<EmployeeReferralsCountRpcResult>(
    'Employee.GetReferralsCount',
  ).then(normalizeEmployeeReferralsCount);
}

export function getEmployeeReferrals(
  input: GetEmployeeReferralsInput = {},
): Promise<EmployeeReferrals> {
  return rpcCall<EmployeeReferralsRpcResult>(
    'Employee.GetReferrals',
    input,
  ).then(normalizeEmployeeReferrals);
}

export function updateEmployeeGoal(
  input: UpdateEmployeeGoalInput,
): Promise<EmployeeGoal> {
  return rpcCall<EmployeeGoal>('Employee.UpdateGoal', input);
}

export function getEmployeeTransactions(
  input: GetEmployeeTransactionsInput = {},
): Promise<EmployeeTransactions> {
  return rpcCall<EmployeeTransactions>('Employee.GetTransactions', input);
}

export function withdrawEmployee(
  input: EmployeeWithdrawInput,
): Promise<unknown> {
  return rpcCall<unknown>('Employee.Withdraw', input);
}

export async function uploadEmployeeAvatar({
  avatar,
  fileName = 'avatar.webp',
  onUploadProgress,
}: UploadEmployeeAvatarInput): Promise<string> {
  const formData = new FormData();
  formData.append('avatar', avatar, fileName);

  const headers: Record<string, string> = {};
  const authToken = getRpcAuthToken();

  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();

    request.open('POST', `${getApiBaseUrl()}/api/user/avatar`);

    for (const [headerName, headerValue] of Object.entries(headers)) {
      request.setRequestHeader(headerName, headerValue);
    }

    request.onerror = () => {
      reject(new RpcError(-32000, 'Network request failed'));
    };

    request.onload = () => {
      onUploadProgress?.(100);

      let payload: UploadEmployeeAvatarHttpResponse;
      try {
        payload = JSON.parse(
          request.responseText || '{}',
        ) as UploadEmployeeAvatarHttpResponse;
      } catch {
        if (request.status < 200 || request.status >= 300) {
          const error = new RpcError(-32000, `HTTP ${request.status}`);
          captureBackendApiError(error, {
            endpoint: 'Employee.UploadAvatar',
            httpStatus: request.status,
            transport: 'http',
          });
          reject(error);
          return;
        }

        const error = new RpcError(-32700, 'Malformed JSON in response');
        captureBackendApiError(error, {
          endpoint: 'Employee.UploadAvatar',
          httpStatus: request.status,
          transport: 'http',
        });
        reject(error);
        return;
      }

      if (request.status < 200 || request.status >= 300) {
        const error = new RpcError(
          -32000,
          payload.message ?? payload.error ?? `HTTP ${request.status}`,
          payload,
        );
        captureBackendApiError(error, {
          endpoint: 'Employee.UploadAvatar',
          httpStatus: request.status,
          transport: 'http',
        });
        reject(error);
        return;
      }

      if (
        typeof payload.result !== 'string' ||
        payload.result.trim().length === 0
      ) {
        const error = new RpcError(
          -32603,
          'Upload avatar response is missing result',
          payload,
        );
        captureBackendApiError(error, {
          endpoint: 'Employee.UploadAvatar',
          httpStatus: request.status,
          transport: 'http',
        });
        reject(error);
        return;
      }

      resolve(payload.result);
    };

    request.upload.onloadstart = () => {
      onUploadProgress?.(0);
    };

    request.upload.onprogress = event => {
      if (!event.lengthComputable || event.total <= 0) {
        return;
      }

      onUploadProgress?.(
        Math.max(
          0,
          Math.min(100, Math.round((event.loaded / event.total) * 100)),
        ),
      );
    };

    request.send(formData);
  });
}
